import { useCallback } from "react";
import { atom, PrimitiveAtom, useAtom } from "jotai";

import { Api, MissingTWithDrop } from "interfaces";

import {
  useGetTypesForFile,
  useGetDroppedColumns,
} from "modules/data-files/api";
import { useCreateProjectWithDataAndWorkspace } from "modules/projects/api";

import { ProjectMetaData } from "flows/impact-analysis-continous/interfaces";
import * as flowConfig from "flows/impact-analysis-continous/config";

import {
  useGetInputFile,
  useGetTimeColumn,
  useGetTargetColumn,
  useGetIDVariables,
  useGetTreatmentDefinition,
  useGetTreatmentTag,
  useGetControlTag,
} from "./store";

const projectCreateStatus = atom<"not-started" | "in-progress" | "completed">(
  "not-started"
);

const projectCreateError = atom<string | Api.ApiError>(null) as PrimitiveAtom<
  string | Api.ApiError
>;

export const useGetCreate = () => {
  const createProject = useCreateProjectWithDataAndWorkspace();

  const inputFile = useGetInputFile();
  const colTypes = useGetTypesForFile(inputFile);
  const droppedColumns = useGetDroppedColumns(inputFile);

  const treatmentDefinition = useGetTreatmentDefinition();
  const targetColumn = useGetTargetColumn();
  const treatmentTag = useGetTreatmentTag();
  const controlTag = useGetControlTag();

  const timeColumn = useGetTimeColumn();
  const idColumns = useGetIDVariables();

  const [status, setStatus] = useAtom(projectCreateStatus);
  const [error, setError] = useAtom(projectCreateError);

  const create = useCallback(async () => {
    if (!inputFile || !targetColumn || treatmentDefinition.length === 0) {
      setError(
        "Input Data, Treatment Definitions, and Target Column ARE ALL MANDATORY!!!"
      );
      setStatus("completed");
      return;
    }
    setStatus("in-progress");
    setError(null);
    const projectMetaData: ProjectMetaData = {
      [flowConfig.Y_COLUMN_KEY]: targetColumn,
      [flowConfig.TREATMENT_FILTERS_KEY]: treatmentDefinition,
      [flowConfig.TREATMENT_TAG]: treatmentTag,
      [flowConfig.CONTROL_TAG]: controlTag,
    };
    const projectSpec = {
      flow: flowConfig.FLOW_NAME,
      data_files: { [flowConfig.INPUT_DATA]: inputFile },
      meta_data: projectMetaData,
    };

    const wsColTypes = { ...colTypes };
    wsColTypes[targetColumn] = "numeric";
    if (timeColumn && wsColTypes[timeColumn] === "char") {
      wsColTypes[timeColumn] = "date";
    }

    const workspaceSpec = {
      key: "init",
      recipe: flowConfig.RECIPE,
      annotations: {
        [flowConfig.RUN_ID]: "INIT",
        [flowConfig.TIME_STAMP]: new Date().valueOf(),
      },
      input_data: { [flowConfig.INPUT_DATA]: [inputFile] },
      cleanup_spec: {
        [inputFile]: {
          column_types: wsColTypes,
          columns_to_drop: droppedColumns.filter(
            (f) => f !== targetColumn && f !== timeColumn
          ),
          missing_treatment: [
            { column: targetColumn, treatment: "drop-rows" },
          ] as MissingTWithDrop[],
        },
      },
      variable_mapping: {
        [flowConfig.Y_COLUMN_KEY]: [targetColumn],
        [flowConfig.TIME_VARIABLE_KEY]: timeColumn ? [timeColumn] : [],
        [flowConfig.ID_VARIABLES_KEY]: idColumns || [],
      },
      model_params: { [flowConfig.TREATMENT_FILTERS_KEY]: treatmentDefinition },
    };

    const resp = await createProject({
      "project-spec": projectSpec,
      "workspace-spec": workspaceSpec,
    });

    if (resp.success) {
    } else if (resp.success === false) {
      setError(resp.error);
    }
    setStatus("completed");
    return resp;
  }, [
    inputFile,
    treatmentDefinition,
    treatmentTag,
    controlTag,
    timeColumn,
    targetColumn,
    idColumns,
    colTypes,
    droppedColumns,
  ]);

  return [create, status, error] as const;
};
